import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
// eslint-disable-next-line import/named
import { select as D3select, easeLinear as D3easeLinear } from 'd3';
import './styles.css';

const measures = {
  width: 38,
  height: 36,
  rect: 6,
  slot: 8, // 8 is rectangle width (6) + separation between them (2)
};

const animation = {
  start: 1, // Photogram number of the start of the animation of the first rectangle
  delta: 4, // Delay between start of animation of each rectangle (in number of photograms)
  duration: 33, // Duration of a complete spin (in number of photograms)
  growth: 6, // Photograms it takes to grow to full height
  delay: 8, // Delay between growth and shrink animation (in number of photograms)
  shrink: 7, // Photograms it takes to shrink back to initial height
};

const generateId = (index) => {
  return `rectangle-${index}`;
};

const rectCount = 5;

class Spinner extends React.Component {
  componentDidMount() {
    const svg = D3select(this.svg);

    for (let index = 0; index < rectCount; index += 1) {
      const x = index * measures.slot;
      const y = (measures.height - measures.rect) / 2;
      svg
        .append('rect')
        .attr('id', generateId(index))
        .attr('class', 'spinner-rectangle')
        .attr('x', x)
        .attr('y', y)
        .attr('width', measures.rect)
        .attr('height', measures.rect);
    }

    if (this.props.active) {
      this.start(this.props);
    }
  }

  // eslint-disable-next-line react/no-deprecated
  componentWillReceiveProps(nextProps) {
    if (nextProps.active !== this.props.active) {
      if (nextProps.active) {
        this.start(nextProps);
      } else {
        this.stop();
      }
    }

    if (nextProps.speed !== this.props.speed) {
      if (nextProps.active) {
        this.stop();
        this.start(nextProps);
      }
    }
  }

  componentWillUnmount() {
    this.stop();
  }

  start(props) {
    clearInterval(this.animation);
    this.animation = setInterval(() => {
      for (let index = 0; index < rectCount; index += 1) {
        const element = D3select(this.svg).select(`#${generateId(index)}`);
        const grow = index * animation.delta + animation.start;
        element
          .transition()
          .delay(grow * props.speed)
          .duration(animation.growth * props.speed)
          .ease(D3easeLinear)
          .attr('y', 0)
          .attr('height', measures.height);

        const shrink = grow + animation.delay;
        element
          .transition()
          .delay(shrink * props.speed)
          .duration(animation.shrink * props.speed)
          .ease(D3easeLinear)
          .attr('y', (measures.height - measures.rect) / 2)
          .attr('height', measures.rect);
      }
    }, animation.duration * props.speed);
  }

  stop() {
    clearInterval(this.animation);
    for (let index = 0; index < rectCount; index += 1) {
      const element = D3select(this.svg).select(`#${generateId(index)}`);
      element.interrupt();
      element
        .attr('y', (measures.height - measures.rect) / 2)
        .attr('height', measures.rect);
    }
  }

  render() {
    const { speed, active, theme, className, ...otherProps } = this.props;

    const classes = classNames(
      'ng-ui-widgets',
      'ng-ui-spinner',
      `theme-${theme}`,
      className,
    );

    return (
      <div {...otherProps} className={classes}>
        <svg
          version="1.1"
          viewBox={`0 0 ${measures.width} ${measures.height}`}
          preserveAspectRatio="xMaxYMax"
          ref={(svg) => {
            this.svg = svg;
          }}
        />
      </div>
    );
  }
}

Spinner.defaultProps = {
  speed: 30,
  active: true,
  theme: 'primary',
  className: '',
};

Spinner.propTypes = {
  speed: PropTypes.number,
  active: PropTypes.bool,
  theme: PropTypes.oneOf(['primary', 'secondary', 'custom']),
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.object,
  ]),
};

export default Spinner;
